/**
 * "Relacionamentos" on the person record (D580): the favourites and blocks the person set (to drivers on a rider record, to riders on
 * a driver record) and the ones others set on them, with the reason and — where the console has the record — a link to the other party.
 */
import { type ReactElement } from 'react';
import { type DirectoryRealm, type DirectoryUser } from '@vello/api-client';
import { Badge, Ink, RecordRow, RecordRowButton, RecordSection, Text } from '@vello/web-theme';
import { screenAllowed } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { PersonCard, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';

interface RelationshipRow {
  readonly id: string;
  readonly kind: string;
  readonly direction: string;
  readonly otherId: string;
  readonly otherName?: string | undefined;
  readonly reasonCode?: string | undefined;
  readonly createdAt: string;
}

export function PersonRelationshipsCard({ user, realm }: { user: DirectoryUser; realm: DirectoryRealm }): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();
  const { state, reload } = useCardRead(() => runtime.personInsights.relationships(user.id), [runtime, user.id]);
  // The other party of a rider's favourite or block is a driver, and the other way round.
  const otherRoute = realm === 'RIDER' ? 'driver_detail' : 'passenger_detail';
  const linkable = screenAllowed(grants, otherRoute);

  const rows = (list: readonly RelationshipRow[]): ReactElement => (
    <>
      {list.map((r) => {
        const body = (
          <>
            <Badge tone={r.kind === 'BLOCK' ? 'danger' : 'success'}>{v('relationships.person_kind', r.kind)}</Badge>
            <Ink strong>{r.otherName ?? t('prec.rel.unnamed')}</Ink>
            {r.reasonCode !== undefined && (
              <Text role="caption" tone="secondary">
                {v('relationships.block_reason', r.reasonCode)}
              </Text>
            )}
            <span className="vello-grow" />
            <Ink mono>{fmt.day(r.createdAt)}</Ink>
          </>
        );
        return linkable ? (
          <RecordRowButton key={r.id} aria-label={t('prec.rel.open', { name: r.otherName ?? t('prec.rel.unnamed') })} onClick={() => navigate(otherRoute, { id: r.otherId })}>
            {body}
          </RecordRowButton>
        ) : (
          <RecordRow key={r.id} dense>
            {body}
          </RecordRow>
        );
      })}
    </>
  );

  return (
    <PersonCard
      title={t('prec.rel.title')}
      what={t('prec.what.relationships')}
      state={state}
      reload={reload}
      isEmpty={(r) => r.relationships.length === 0}
      empty={t('prec.rel.empty')}
    >
      {(r) => {
        const byPerson = r.relationships.filter((x) => x.direction === 'BY_PERSON');
        const ofPerson = r.relationships.filter((x) => x.direction === 'OF_PERSON');
        return (
          <>
            {byPerson.length > 0 && (
              <RecordSection>
                <Text as="h3" role="label">
                  {v('relationships.person_direction', 'BY_PERSON')}
                </Text>
                {rows(byPerson)}
              </RecordSection>
            )}
            {ofPerson.length > 0 && (
              <RecordSection>
                <Text as="h3" role="label">
                  {v('relationships.person_direction', 'OF_PERSON')}
                </Text>
                {rows(ofPerson)}
              </RecordSection>
            )}
          </>
        );
      }}
    </PersonCard>
  );
}
